import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import { geminiService } from '../services/geminiService'
import { budgetService } from '../services/budgetService'
import { 
  ChatSession, 
  ChatMessage, 
  UseAIChatReturn, 
  GeminiChatRequest,
  BudgetRecommendation
} from '../types/ai-budget'

export const useAIChat = (): UseAIChatReturn => {
  const { user } = useAuth()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null)
  const [latestBudgetRecommendation, setLatestBudgetRecommendation] = useState<BudgetRecommendation | null>(null)
  const [budgetMonthlyIncome, setBudgetMonthlyIncome] = useState<number>(0)

  // Create a new chat session
  const createNewSession = useCallback(async (): Promise<ChatSession> => {
    if (!user) {
      throw new Error('User not authenticated')
    }

    console.log('🏗️ useAIChat: Creating new session for user:', user.id)

    try {
      setError(null)
      setIsLoading(true)

      // First, mark current session as completed if it exists
      if (currentSession) {
        console.log('📋 Marking previous session as completed:', currentSession.id)
        await supabase
          .from('ai_chat_sessions')
          .update({ status: 'completed' })
          .eq('id', currentSession.id)
      }

      const { data: session, error: sessionError } = await supabase
        .from('ai_chat_sessions')
        .insert([
          {
            user_id: user.id,
            title: `Budget Planning Session - ${new Date().toLocaleDateString()}`,
            status: 'active'
          }
        ])
        .select()
        .single()

      if (sessionError) {
        console.error('❌ Database error creating session:', sessionError)
        console.error('Error details:', {
          code: sessionError.code,
          message: sessionError.message,
          details: sessionError.details,
          hint: sessionError.hint
        })
        
        if (sessionError.code === '42P01') {
          throw new Error('Database table "ai_chat_sessions" does not exist. Please run the database setup.')
        } else {
          throw new Error(`Database error: ${sessionError.message}`)
        }
      }

      console.log('✅ New session created in database:', session.id)
      
      console.log('🔄 Before state update:', {
        oldSessionId: currentSession?.id,
        oldMessagesCount: messages.length,
        newSessionId: session.id
      })
      
      setCurrentSession(session)
      setMessages([]) // Clear messages for new session
      
      console.log('🔄 After state update calls made - waiting for re-render')
      
      // Force a small delay to ensure state updates are processed
      setTimeout(() => {
        console.log('✨ State should now be updated:', {
          sessionId: session.id,
          shouldHaveZeroMessages: true
        })
      }, 100)
      
      return session
    } catch (err) {
      console.error('❌ useAIChat createNewSession error:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to create session'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [user, currentSession])

  // Load an existing session
  const loadSession = useCallback(async (sessionId: string): Promise<void> => {
    if (!user) {
      throw new Error('User not authenticated')
    }

    try {
      setIsLoading(true)
      setError(null)

      // Load session details
      const { data: session, error: sessionError } = await supabase
        .from('ai_chat_sessions')
        .select('*')
        .eq('id', sessionId)
        .eq('user_id', user.id)
        .single()

      if (sessionError) {
        console.error('Failed to load session:', sessionError)
        throw new Error('Failed to load chat session')
      }

      // Load messages for this session
      const { data: sessionMessages, error: messagesError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true })

      if (messagesError) {
        console.error('Failed to load messages:', messagesError)
        throw new Error('Failed to load chat messages')
      }

      setCurrentSession(session)
      setMessages(sessionMessages || [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load session'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [user])

  // Send a message in the current session
  const sendMessage = useCallback(async (content: string): Promise<void> => {
    if (!user || !currentSession) {
      throw new Error('No active session')
    }

    // Check if service is available and wait for initialization if needed
    const isServiceAvailable = await geminiService.checkAvailability()
    if (!isServiceAvailable) {
      throw new Error('AI service is not available. Please check your API key configuration and internet connection.')
    }

    try {
      setIsLoading(true)
      setError(null)

      // Save user message to database
      const { data: userMessage, error: userMessageError } = await supabase
        .from('chat_messages')
        .insert([
          {
            session_id: currentSession.id,
            role: 'user',
            content: content.trim(),
            metadata: {}
          }
        ])
        .select()
        .single()

      if (userMessageError) {
        console.error('Failed to save user message:', userMessageError)
        throw new Error('Failed to save your message')
      }

      // Update local state with user message
      setMessages(prev => [...prev, userMessage])

      // Prepare AI request
      const aiRequest: GeminiChatRequest = {
        message: content,
        context: messages.slice(-10), // Last 10 messages for context
        userId: user.id,
        sessionId: currentSession.id
      }

      // Get AI response
      const aiResponse = await geminiService.sendChatMessage(aiRequest)

      // Save AI response to database
      const { data: aiMessage, error: aiMessageError } = await supabase
        .from('chat_messages')
        .insert([
          {
            session_id: currentSession.id,
            role: 'assistant',
            content: aiResponse.message,
            metadata: aiResponse.metadata || {}
          }
        ])
        .select()
        .single()

      if (aiMessageError) {
        console.error('Failed to save AI message:', aiMessageError)
        throw new Error('Failed to save AI response')
      }

      // Update local state with AI message
      setMessages(prev => [...prev, aiMessage])

      // Check if AI response contains a budget recommendation
      if (aiResponse.budgetRecommendation) {
        console.log('💰 useAIChat: Budget recommendation detected, storing for potential save')
        setLatestBudgetRecommendation(aiResponse.budgetRecommendation)
        
        // Try to extract monthly income from the conversation
        const income = extractMonthlyIncomeFromMessages([...messages, { ...aiMessage, content: content }])
        setBudgetMonthlyIncome(income)
      }

      // Update session timestamp
      await supabase
        .from('ai_chat_sessions')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', currentSession.id)

    } catch (err) {
      console.error('Error in sendMessage:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [user, currentSession, messages])

  // Clear error state
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Initialize a session on mount if user exists
  useEffect(() => {
    const initializeSession = async () => {
      if (user && !currentSession) {
        try {
          // Try to load the most recent active session
          const { data: recentSessions, error } = await supabase
            .from('ai_chat_sessions')
            .select('*')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .order('updated_at', { ascending: false })
            .limit(1)

          if (error) {
            console.error('Failed to load recent sessions:', error)
            return
          }

          if (recentSessions && recentSessions.length > 0) {
            // Load the most recent session
            await loadSession(recentSessions[0].id)
          } else {
            // Create a new session if none exists
            await createNewSession()
          }
        } catch (err) {
          console.error('Failed to initialize session:', err)
          // Don't throw here, let the user manually create a session
        }
      }
    }

    initializeSession()
  }, [user, currentSession, loadSession, createNewSession])

  // Clean up on unmount
  useEffect(() => {
    return () => {
      setMessages([])
      setCurrentSession(null)
      setError(null)
    }
  }, [])

  // Save budget recommendation to database
  const saveBudget = useCallback(async (budgetName: string, description?: string): Promise<void> => {
    if (!user || !currentSession || !latestBudgetRecommendation) {
      throw new Error('Cannot save budget: Missing required data')
    }

    console.log('💾 useAIChat: Saving budget to database...', {
      budgetName,
      sessionId: currentSession.id,
      monthlyIncome: budgetMonthlyIncome
    })

    try {
      await budgetService.saveBudget({
        userId: user.id,
        sessionId: currentSession.id,
        name: budgetName,
        description,
        monthlyIncome: budgetMonthlyIncome,
        budgetRecommendation: latestBudgetRecommendation
      })
      
      console.log('✅ useAIChat: Budget saved successfully')
      
      // Clear the recommendation after successful save
      setLatestBudgetRecommendation(null)
      setBudgetMonthlyIncome(0)
    } catch (error) {
      console.error('❌ useAIChat: Error saving budget:', error)
      throw error
    }
  }, [user, currentSession, latestBudgetRecommendation, budgetMonthlyIncome])

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearError,
    createNewSession,
    loadSession,
    currentSession,
    latestBudgetRecommendation,
    budgetMonthlyIncome,
    saveBudget
  }
}

// Helper function to extract monthly income from messages
const extractMonthlyIncomeFromMessages = (messages: ChatMessage[]): number => {
  for (const message of messages.reverse()) {
    if (message.role === 'user') {
      const incomeMatch = message.content.match(/income.*?[\$]?(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/i) ||
                         message.content.match(/make.*?[\$]?(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/i) ||
                         message.content.match(/earn.*?[\$]?(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/i)
      
      if (incomeMatch) {
        const income = parseFloat(incomeMatch[1].replace(/,/g, ''))
        if (income > 500 && income < 50000) { // Reasonable monthly income range
          return income
        }
      }
    }
  }
  
  // Default fallback
  return 5000
}

